import React from "react";
import { useTranslation } from "react-i18next";

import {
    Card,
    CardTitle,
    SectionHeader,
    ResetIconButton,
    SmallToggle,
    LanguageItem,
    LanguageCodeBadge,
    LanguageLabelText,
    DefaultLanguageBadge,
} from "./styled";
import { ResetIcon } from "./ResetIcon";
import { LANGUAGES } from "./types";

type LanguagesCardProps = {
    enabledLanguages: Record<string, boolean>;
    defaultLanguage: string;
    mainColor: string;
    onToggleLanguage: (code: string) => void;
    onSetDefault: (code: string) => void;
    onReset: () => void;
};

export function LanguagesCard({
    enabledLanguages,
    defaultLanguage,
    mainColor,
    onToggleLanguage,
    onSetDefault,
    onReset,
}: LanguagesCardProps): JSX.Element {
    const { t } = useTranslation();

    return (
        <Card>
            <SectionHeader>
                <CardTitle style={{ margin: 0, padding: 0, border: "none" }}>
                    {t("Languages")}
                </CardTitle>
                <ResetIconButton
                    data-testid="servio-langues-reset-btn"
                    mainColor={mainColor}
                    onClick={onReset}
                    title={t("Reset to default")}
                >
                    <ResetIcon />
                    {t("Reset")}
                </ResetIconButton>
            </SectionHeader>

            {LANGUAGES.map((lang) => {
                const isEnabled = enabledLanguages[lang.code];
                const isDefault = defaultLanguage === lang.code;
                return (
                    <LanguageItem
                        key={lang.code}
                        data-testid={`servio-langues-item-${lang.code}`}
                    >
                        <LanguageCodeBadge
                            enabled={isEnabled}
                            mainColor={mainColor}
                        >
                            {lang.code.toUpperCase()}
                        </LanguageCodeBadge>
                        <LanguageLabelText>{lang.label}</LanguageLabelText>
                        <DefaultLanguageBadge
                            data-testid={`servio-langues-default-${lang.code}-btn`}
                            isDefault={isDefault}
                            mainColor={mainColor}
                            onClick={() => onSetDefault(lang.code)}
                            disabled={isDefault}
                        >
                            {isDefault ? t("Default") : t("Set as default")}
                        </DefaultLanguageBadge>
                        <SmallToggle
                            data-testid={`servio-langues-toggle-${lang.code}`}
                            isActive={isEnabled}
                            mainColor={mainColor}
                            onClick={() => onToggleLanguage(lang.code)}
                        />
                    </LanguageItem>
                );
            })}
        </Card>
    );
}
